import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  TrendingUp, ArrowLeft, Plus, Trash2, Edit2, DollarSign, PiggyBank,
  CreditCard, Briefcase, Calculator, BarChart3, Info, Wallet, Building, Gem, Coins
} from 'lucide-react';
import { financeApi, tokenApi } from '@/lib/api';
import { toast } from 'sonner';
import { formatDate, isAuthenticated } from '@/lib/utils';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import SponsorSection from '@/components/SponsorSection';
import { ModuleChat } from '@/components/ModuleChat';

const ASSET_TYPES = [
  { value: 'cash', label: 'Cash', icon: Wallet },
  { value: 'savings', label: 'Savings', icon: PiggyBank },
  { value: 'stocks', label: 'Stocks', icon: TrendingUp },
  { value: 'crypto', label: 'Crypto', icon: Coins },
  { value: 'mutual_funds', label: 'Mutual Funds', icon: BarChart3 },
  { value: 'mp2', label: 'MP2', icon: Building },
  { value: 'real_estate', label: 'Real Estate', icon: Building },
  { value: 'gold', label: 'Gold / Silver', icon: Gem },
  { value: 'other', label: 'Other', icon: DollarSign },
];

const LIABILITY_TYPES = ['Credit Card', 'Personal Loan', 'Mortgage', 'Car Loan', 'Student Loan', 'Other'];
const INCOME_TYPES = ['Salary', 'Business', 'Freelance', 'Rental Income', 'Dividends', 'Interest', 'Royalties', 'Other'];
const FREQUENCIES = ['monthly', 'annually'];

const PIE_COLORS = ['#2563EB', '#7C3AED', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#8B5CF6', '#EF4444', '#14B8A6'];

const fmt = (v) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

export default function WealthCompassModule() {
  const { tokenId } = useParams();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [incomeStreams, setIncomeStreams] = useState([]);
  const [projections, setProjections] = useState([]);
  const [sponsorInfo, setSponsorInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialogs
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showAddLiability, setShowAddLiability] = useState(false);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showProjection, setShowProjection] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editingLiability, setEditingLiability] = useState(null);
  const [editingIncome, setEditingIncome] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Asset form
  const [aType, setAType] = useState('cash');
  const [aName, setAName] = useState('');
  const [aValue, setAValue] = useState('');

  // Liability form
  const [lType, setLType] = useState('Credit Card');
  const [lName, setLName] = useState('');
  const [lAmount, setLAmount] = useState('');
  const [lRate, setLRate] = useState('');

  // Income form
  const [iType, setIType] = useState('Salary');
  const [iName, setIName] = useState('');
  const [iAmount, setIAmount] = useState('');
  const [iFreq, setIFreq] = useState('monthly');

  // Projection form
  const [pMonthly, setPMonthly] = useState('5000');
  const [pYears, setPYears] = useState('5');
  const [pReturn, setPReturn] = useState('7');
  const [projResult, setProjResult] = useState(null);

  useEffect(() => {
    if (!isAuthenticated()) { navigate(`/f/${tokenId}`); return; }
    loadData();
    loadSponsorInfo();
  }, [tokenId]);

  const loadData = async () => {
    try {
      const [dashRes, assetsRes, liabRes, incRes, projRes] = await Promise.all([
        financeApi.getDashboard(tokenId),
        financeApi.getAssets(tokenId),
        financeApi.getLiabilities(tokenId),
        financeApi.getIncomeStreams(tokenId),
        financeApi.getProjections(tokenId),
      ]);
      setDashboard(dashRes.data);
      setAssets(assetsRes.data);
      setLiabilities(liabRes.data);
      setIncomeStreams(incRes.data);
      setProjections(projRes.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setIsLoading(false); }
  };

  const loadSponsorInfo = async () => {
    try { const r = await tokenApi.getSponsorInfo(tokenId, 'finance'); setSponsorInfo(r.data); } catch {}
  };

  // Asset handlers
  const handleSaveAsset = async () => {
    if (!aName || !aValue) return;
    setIsSubmitting(true);
    try {
      if (editingAsset) {
        await financeApi.updateAsset(editingAsset.id, { asset_type: aType, name: aName, value: parseFloat(aValue) });
        toast.success('Asset updated!');
      } else {
        await financeApi.createAsset({ token_id: tokenId, asset_type: aType, name: aName, value: parseFloat(aValue) });
        toast.success('Asset added!');
      }
      closeAssetDialog(); loadData();
    } catch (error) { toast.error('Failed'); }
    finally { setIsSubmitting(false); }
  };

  const openEditAsset = (a) => { setAType(a.asset_type); setAName(a.name); setAValue(String(a.value)); setEditingAsset(a); setShowAddAsset(true); };
  const closeAssetDialog = () => { setShowAddAsset(false); setEditingAsset(null); setAType('cash'); setAName(''); setAValue(''); };

  // Liability handlers
  const handleSaveLiability = async () => {
    if (!lName || !lAmount) return;
    setIsSubmitting(true);
    try {
      if (editingLiability) {
        await financeApi.updateLiability(editingLiability.id, { liability_type: lType, name: lName, amount: parseFloat(lAmount), interest_rate: lRate ? parseFloat(lRate) : undefined });
        toast.success('Liability updated!');
      } else {
        await financeApi.createLiability({ token_id: tokenId, liability_type: lType, name: lName, amount: parseFloat(lAmount), interest_rate: lRate ? parseFloat(lRate) : undefined });
        toast.success('Liability added!');
      }
      closeLiabilityDialog(); loadData();
    } catch (error) { toast.error('Failed'); }
    finally { setIsSubmitting(false); }
  };

  const openEditLiability = (l) => { setLType(l.liability_type); setLName(l.name); setLAmount(String(l.amount)); setLRate(l.interest_rate ? String(l.interest_rate) : ''); setEditingLiability(l); setShowAddLiability(true); };
  const closeLiabilityDialog = () => { setShowAddLiability(false); setEditingLiability(null); setLType('Credit Card'); setLName(''); setLAmount(''); setLRate(''); };

  // Income handlers
  const handleSaveIncome = async () => {
    if (!iName || !iAmount) return;
    setIsSubmitting(true);
    try {
      if (editingIncome) {
        await financeApi.updateIncome(editingIncome.id, { income_type: iType, name: iName, amount: parseFloat(iAmount), frequency: iFreq });
        toast.success('Income updated!');
      } else {
        await financeApi.createIncome({ token_id: tokenId, income_type: iType, name: iName, amount: parseFloat(iAmount), frequency: iFreq });
        toast.success('Income added!');
      }
      closeIncomeDialog(); loadData();
    } catch (error) { toast.error('Failed'); }
    finally { setIsSubmitting(false); }
  };

  const openEditIncome = (s) => { setIType(s.income_type); setIName(s.name); setIAmount(String(s.amount)); setIFreq(s.frequency); setEditingIncome(s); setShowAddIncome(true); };
  const closeIncomeDialog = () => { setShowAddIncome(false); setEditingIncome(null); setIType('Salary'); setIName(''); setIAmount(''); setIFreq('monthly'); };

  // Projection handler
  const handleRunProjection = async () => {
    if (!pMonthly || !pYears || !pReturn) return;
    setIsSubmitting(true);
    try {
      const res = await financeApi.createProjection({
        token_id: tokenId, monthly_contribution: parseFloat(pMonthly),
        duration_months: parseInt(pYears) * 12, annual_return: parseFloat(pReturn),
      });
      setProjResult(res.data);
      loadData();
    } catch (error) { toast.error('Failed'); }
    finally { setIsSubmitting(false); }
  };

  // Pie chart data
  const assetChartData = dashboard?.assets_by_type ? Object.entries(dashboard.assets_by_type).map(([type, value]) => {
    const conf = ASSET_TYPES.find(a => a.value === type) || { label: type };
    return { name: conf.label, value };
  }) : [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="wealth-compass-module">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-slate-400 hover:text-slate-600" data-testid="back-to-dashboard"><ArrowLeft className="w-6 h-6" /></Link>
            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center"><TrendingUp className="w-5 h-5 text-violet-600" /></div>
            <div>
              <h1 className="font-bold text-lg text-slate-900">Memora Wealth Compass</h1>
              <p className="text-xs text-slate-500 font-mono">{tokenId}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Net Worth', value: fmt(dashboard?.net_worth || 0), color: dashboard?.net_worth >= 0 ? 'text-emerald-600' : 'text-red-600', icon: DollarSign },
            { label: 'Total Assets', value: fmt(dashboard?.total_assets || 0), color: 'text-blue-600', icon: PiggyBank },
            { label: 'Liabilities', value: fmt(dashboard?.total_liabilities || 0), color: 'text-red-500', icon: CreditCard },
            { label: 'Passive Income', value: `${fmt(dashboard?.passive_income || 0)}/mo`, color: 'text-violet-600', icon: Briefcase, subtitle: `${dashboard?.passive_ratio || 0}% ratio` },
          ].map((card, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="flex items-center gap-2 mb-1"><card.icon className="w-4 h-4 text-slate-400" /><span className="text-xs text-slate-500">{card.label}</span></div>
              <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
              {card.subtitle && <p className="text-xs text-slate-400">{card.subtitle}</p>}
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-100 rounded-xl mb-6 p-1 w-full grid grid-cols-4">
            <TabsTrigger value="overview" className="rounded-lg text-xs sm:text-sm" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="liabilities" className="rounded-lg text-xs sm:text-sm" data-testid="tab-liabilities">Liabilities</TabsTrigger>
            <TabsTrigger value="income" className="rounded-lg text-xs sm:text-sm" data-testid="tab-income">Income</TabsTrigger>
            <TabsTrigger value="projections" className="rounded-lg text-xs sm:text-sm" data-testid="tab-projections">Projections</TabsTrigger>
          </TabsList>

          {/* Overview / Assets Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Asset List */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-slate-900">Assets</h2>
                  <Button onClick={() => { closeAssetDialog(); setShowAddAsset(true); }} className="bg-violet-500 hover:bg-violet-600 text-white rounded-full" size="sm" data-testid="add-asset-btn"><Plus className="w-4 h-4 mr-1" /> Add Asset</Button>
                </div>
                {assets.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center"><PiggyBank className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No assets yet</p></div>
                ) : (
                  <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                    {assets.map((a) => {
                      const conf = ASSET_TYPES.find(t => t.value === a.asset_type) || ASSET_TYPES[8];
                      const Icon = conf.icon;
                      return (
                        <div key={a.id} className="p-4 flex items-center justify-between" data-testid={`asset-${a.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><Icon className="w-5 h-5 text-blue-600" /></div>
                            <div><p className="font-medium text-slate-900">{a.name}</p><p className="text-xs text-slate-500 capitalize">{conf.label}</p></div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{fmt(a.value)}</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditAsset(a)}><Edit2 className="w-3.5 h-3.5 text-slate-400" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => { await financeApi.deleteAsset(a.id); toast.success('Deleted'); loadData(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Asset Distribution Chart */}
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-4">Asset Distribution</h2>
                {assetChartData.length > 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-100 p-6">
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={assetChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {assetChartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v) => fmt(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-400">Add assets to see distribution</div>
                )}

                {/* Market Watch */}
                <div className="mt-6">
                  <h3 className="text-base font-bold text-slate-900 mb-3">Market Watch</h3>
                  <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                    {[
                      { name: 'Gold (per oz)', price: '$2,340', change: '+0.8%', up: true },
                      { name: 'Silver (per oz)', price: '$28.50', change: '+1.2%', up: true },
                      { name: 'S&P 500 Index', price: '5,480', change: '-0.3%', up: false },
                      { name: 'PHP/USD Rate', price: '55.80', change: '+0.1%', up: true },
                    ].map((item, i) => (
                      <div key={i} className="p-3 flex items-center justify-between">
                        <span className="text-sm text-slate-700">{item.name}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-slate-900">{item.price}</span>
                          <span className={`text-xs ml-2 ${item.up ? 'text-emerald-600' : 'text-red-500'}`}>{item.change}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Info className="w-3 h-3" />Prices and projections are illustrative and not financial advice.</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Liabilities Tab */}
          <TabsContent value="liabilities">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Liabilities</h2>
              <Button onClick={() => { closeLiabilityDialog(); setShowAddLiability(true); }} className="bg-red-500 hover:bg-red-600 text-white rounded-full" size="sm" data-testid="add-liability-btn"><Plus className="w-4 h-4 mr-1" /> Add Liability</Button>
            </div>
            {liabilities.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center"><CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No liabilities recorded</p></div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                {liabilities.map((l) => (
                  <div key={l.id} className="p-4 flex items-center justify-between" data-testid={`liability-${l.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center"><CreditCard className="w-5 h-5 text-red-500" /></div>
                      <div><p className="font-medium text-slate-900">{l.name}</p><p className="text-xs text-slate-500">{l.liability_type}{l.interest_rate ? ` · ${l.interest_rate}% APR` : ''}</p></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-red-600">{fmt(l.amount)}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditLiability(l)}><Edit2 className="w-3.5 h-3.5 text-slate-400" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => { await financeApi.deleteLiability(l.id); toast.success('Deleted'); loadData(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Income Tab */}
          <TabsContent value="income">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Income Streams</h2>
              <Button onClick={() => { closeIncomeDialog(); setShowAddIncome(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" size="sm" data-testid="add-income-btn"><Plus className="w-4 h-4 mr-1" /> Add Income</Button>
            </div>
            {incomeStreams.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center"><Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No income streams yet</p></div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                {incomeStreams.map((s) => (
                  <div key={s.id} className="p-4 flex items-center justify-between" data-testid={`income-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"><Briefcase className="w-5 h-5 text-emerald-600" /></div>
                      <div><p className="font-medium text-slate-900">{s.name}</p><p className="text-xs text-slate-500">{s.income_type} · {s.frequency}</p></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-emerald-600">{fmt(s.amount)}<span className="text-xs text-slate-400">/{s.frequency === 'monthly' ? 'mo' : 'yr'}</span></span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditIncome(s)}><Edit2 className="w-3.5 h-3.5 text-slate-400" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => { await financeApi.deleteIncome(s.id); toast.success('Deleted'); loadData(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Projections Tab */}
          <TabsContent value="projections">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Calculator */}
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-4">Investment Projection Calculator</h2>
                <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
                  <div><label className="text-sm font-medium text-slate-700 mb-1 block">Monthly Contribution</label><Input type="number" value={pMonthly} onChange={e => setPMonthly(e.target.value)} placeholder="5000" className="rounded-xl" data-testid="proj-monthly" /></div>
                  <div><label className="text-sm font-medium text-slate-700 mb-1 block">Investment Duration (years)</label><Input type="number" value={pYears} onChange={e => setPYears(e.target.value)} placeholder="5" className="rounded-xl" data-testid="proj-years" /></div>
                  <div><label className="text-sm font-medium text-slate-700 mb-1 block">Expected Annual Return (%)</label><Input type="number" step="0.1" value={pReturn} onChange={e => setPReturn(e.target.value)} placeholder="7" className="rounded-xl" data-testid="proj-return" /></div>
                  <Button onClick={handleRunProjection} disabled={isSubmitting || !pMonthly || !pYears || !pReturn} className="w-full bg-violet-500 hover:bg-violet-600 text-white rounded-full" data-testid="run-projection-btn">
                    <Calculator className="w-4 h-4 mr-2" />{isSubmitting ? 'Calculating...' : 'Calculate Projection'}
                  </Button>

                  {projResult && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-violet-50 rounded-xl p-4 space-y-2 mt-4" data-testid="projection-result">
                      <div className="flex justify-between"><span className="text-sm text-slate-600">Total Invested</span><span className="font-semibold text-slate-900">{fmt(projResult.total_invested)}</span></div>
                      <div className="flex justify-between"><span className="text-sm text-slate-600">Projected Value</span><span className="font-bold text-violet-600 text-lg">{fmt(projResult.projected_value)}</span></div>
                      <div className="flex justify-between"><span className="text-sm text-slate-600">Estimated Gain</span><span className="font-semibold text-emerald-600">{fmt(projResult.estimated_gain)}</span></div>
                    </motion.div>
                  )}

                  <p className="text-xs text-slate-400 flex items-center gap-1"><Info className="w-3 h-3" />Projections are illustrative and not financial advice.</p>
                </div>
              </div>

              {/* Past Projections */}
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-4">Saved Projections</h2>
                {projections.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center"><Calculator className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">Run a projection to get started</p></div>
                ) : (
                  <div className="space-y-3">
                    {projections.map((p) => (
                      <div key={p.id} className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`projection-${p.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{fmt(p.monthly_contribution)}/mo for {Math.round(p.duration_months / 12)} yrs at {p.annual_return}%</p>
                            <div className="flex items-center gap-4 mt-1">
                              <span className="text-xs text-slate-500">Invested: {fmt(p.total_invested)}</span>
                              <span className="text-xs text-violet-600 font-semibold">Value: {fmt(p.projected_value)}</span>
                              <span className="text-xs text-emerald-600">Gain: {fmt(p.estimated_gain)}</span>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">{formatDate(p.created_at)}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => { await financeApi.deleteProjection(p.id); toast.success('Deleted'); loadData(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Sponsor Section */}
        <SponsorSection sponsorInfo={sponsorInfo} tokenId={tokenId} moduleType="finance" className="mt-8" />
      </main>

      <ModuleChat sponsorInfo={sponsorInfo} />

      {/* Add/Edit Asset Dialog */}
      <Dialog open={showAddAsset} onOpenChange={(o) => { if (!o) closeAssetDialog(); else setShowAddAsset(true); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editingAsset ? 'Edit Asset' : 'Add Asset'}</DialogTitle><DialogDescription>Track your wealth</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
              <Select value={aType} onValueChange={setAType}><SelectTrigger className="rounded-xl" data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ASSET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Name</label><Input value={aName} onChange={e => setAName(e.target.value)} placeholder="BDO Savings Account" className="rounded-xl" data-testid="asset-name" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Value</label><Input type="number" value={aValue} onChange={e => setAValue(e.target.value)} placeholder="0" className="rounded-xl" data-testid="asset-value" /></div>
            <Button onClick={handleSaveAsset} disabled={isSubmitting || !aName || !aValue} className="w-full bg-violet-500 hover:bg-violet-600 text-white rounded-full" data-testid="save-asset-btn">{isSubmitting ? 'Saving...' : editingAsset ? 'Update Asset' : 'Add Asset'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Liability Dialog */}
      <Dialog open={showAddLiability} onOpenChange={(o) => { if (!o) closeLiabilityDialog(); else setShowAddLiability(true); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editingLiability ? 'Edit Liability' : 'Add Liability'}</DialogTitle><DialogDescription>Track what you owe</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
              <Select value={lType} onValueChange={setLType}><SelectTrigger className="rounded-xl" data-testid="liability-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LIABILITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Name</label><Input value={lName} onChange={e => setLName(e.target.value)} placeholder="BPI Credit Card" className="rounded-xl" data-testid="liability-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Amount</label><Input type="number" value={lAmount} onChange={e => setLAmount(e.target.value)} className="rounded-xl" data-testid="liability-amount" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Interest Rate %</label><Input type="number" step="0.1" value={lRate} onChange={e => setLRate(e.target.value)} className="rounded-xl" /></div>
            </div>
            <Button onClick={handleSaveLiability} disabled={isSubmitting || !lName || !lAmount} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="save-liability-btn">{isSubmitting ? 'Saving...' : editingLiability ? 'Update' : 'Add Liability'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Income Dialog */}
      <Dialog open={showAddIncome} onOpenChange={(o) => { if (!o) closeIncomeDialog(); else setShowAddIncome(true); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editingIncome ? 'Edit Income' : 'Add Income'}</DialogTitle><DialogDescription>Track your income sources</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
              <Select value={iType} onValueChange={setIType}><SelectTrigger className="rounded-xl" data-testid="income-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{INCOME_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Name</label><Input value={iName} onChange={e => setIName(e.target.value)} placeholder="Main Salary" className="rounded-xl" data-testid="income-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Amount</label><Input type="number" value={iAmount} onChange={e => setIAmount(e.target.value)} className="rounded-xl" data-testid="income-amount" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Frequency</label>
                <Select value={iFreq} onValueChange={setIFreq}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQUENCIES.map(f => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleSaveIncome} disabled={isSubmitting || !iName || !iAmount} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" data-testid="save-income-btn">{isSubmitting ? 'Saving...' : editingIncome ? 'Update' : 'Add Income'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
